import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

// Closer to the end than this, the user is reading the newest messages.
const NEAR_END = 120;
// Momentum keeps a flick scrolling after the finger is lifted.
const USER_SCROLL_SETTLE_MS = 600;
// The keyboard opening or closing, not the browser bars or a pan.
const KEYBOARD_RESIZE = 100;

interface ChatScroll {
  // Brings the end of the chat into view and keeps following it.
  scrollToEnd: () => void;
}

export const ChatScrollContext = createContext<ChatScroll | null>(null);

export const useChatScroll = () => useContext(ChatScrollContext);

// Bottom of what the user sees: the visual viewport, which the iOS keyboard
// shrinks without shrinking the page, or the desktop card around the chat.
const getVisibleBottom = (container: HTMLElement) => {
  const viewport = window.visualViewport;
  const viewportBottom = viewport
    ? viewport.offsetTop + viewport.height
    : window.innerHeight;
  return Math.min(viewportBottom, container.getBoundingClientRect().bottom);
};

/**
 * Keeps the end of the chat in view while it grows (typed lines, chips,
 * offer cards, late images and fonts) and when the keyboard opens, unless the
 * user scrolled up to read. New messages always bring the end back.
 * `isReady`: the chat is rendered, not its loader.
 */
export const useChatAutoScroll = (isReady: boolean) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const isFollowing = useRef(true);

  const scrollToEnd = useCallback(() => {
    const root = rootRef.current;
    const end = endRef.current;
    isFollowing.current = true;
    if (!root || !end) {
      return;
    }

    // Only ever down, by what is hidden: with the keyboard open the page
    // scrolls past its own end, and aligning it back would fight the user.
    const hidden = end.getBoundingClientRect().bottom - getVisibleBottom(root);
    if (hidden <= 1) {
      return;
    }
    const scroller =
      getComputedStyle(root).overflowY === "auto" ? root : window;
    scroller.scrollBy(0, Math.ceil(hidden));
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    const content = contentRef.current;
    const end = endRef.current;
    if (!isReady || !root || !content || !end) {
      return;
    }

    const onScroll = () => {
      isFollowing.current =
        end.getBoundingClientRect().bottom - getVisibleBottom(root) < NEAR_END;
    };

    // Never scroll under the user's finger, wheel or momentum.
    let isTouching = false;
    let lastUserScroll = 0;
    const onTouchStart = () => {
      isTouching = true;
    };
    const onUserScroll = () => {
      isTouching = false;
      lastUserScroll = Date.now();
    };
    const isUserScrolling = () =>
      isTouching || Date.now() - lastUserScroll < USER_SCROLL_SETTLE_MS;

    const follow = () => {
      if (isFollowing.current && !isUserScrolling()) {
        scrollToEnd();
      }
    };

    let viewportHeight = window.visualViewport?.height ?? 0;
    const onViewportResize = () => {
      const height = window.visualViewport?.height ?? 0;
      if (Math.abs(height - viewportHeight) < KEYBOARD_RESIZE) {
        return;
      }
      viewportHeight = height;
      follow();
    };

    // Window scroll on phones, the card's own scroll on desktop.
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    const passive = { passive: true };
    document.addEventListener("touchstart", onTouchStart, passive);
    document.addEventListener("touchend", onUserScroll, passive);
    document.addEventListener("touchcancel", onUserScroll, passive);
    document.addEventListener("wheel", onUserScroll, passive);
    window.visualViewport?.addEventListener("resize", onViewportResize);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(follow);
    observer?.observe(content);
    scrollToEnd();

    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onUserScroll);
      document.removeEventListener("touchcancel", onUserScroll);
      document.removeEventListener("wheel", onUserScroll);
      window.visualViewport?.removeEventListener("resize", onViewportResize);
      observer?.disconnect();
    };
  }, [isReady, scrollToEnd]);

  return { rootRef, contentRef, endRef, scrollToEnd };
};
